/**
 * PrismPlayerPanel — ABET TV, the live channel player, on the homepage.
 *
 * WHY AN IFRAME AND NOT A PORT
 *   * prism.js loads hls.js and dash.js from jsDelivr at runtime and proxies
 *     every stream through iptv-stream-proxy.abetscrape.workers.dev. Porting
 *     drags that whole external-script surface onto the dashboard's origin,
 *     where a CDN block or a stream failure could reach the odds terminal.
 *   * The dashboard's main chunk is already ~991 kB, which the live-odds
 *     receipt calls a release blocker for bounded loading. prism.js (33 kB) +
 *     catalog.js (9 kB) + a 40,766-channel catalog would make a flagged
 *     problem measurably worse.
 *   * prism ships from its own repo on its own Cloudflare Pages CI. A port
 *     forks it, and every future prism fix would have to be made twice.
 *
 * Verified frameable 2026-08-28: prism-tv.pages.dev sends no X-Frame-Options
 * and no Content-Security-Policy, so no frame-ancestors allowance is needed.
 *
 * LAZY BY DESIGN. The <iframe> is not mounted until the panel is expanded, so
 * a collapsed panel costs nothing — no catalog fetch, no CDN scripts, no
 * player. Collapsing unmounts it again, which also stops audio; leaving a
 * hidden iframe alive would keep a stream playing behind the page.
 *
 * CSS prefix: ppp-
 */

import { useState, useCallback, useRef } from 'react'
import { ChevronDown, ExternalLink } from 'lucide-react'
import AbetChipLoader from './AbetChipLoader'
import './PrismPlayerPanel.css'

// The player is hosted at prism-tv.pages.dev, but it is ABET TV to the
// customer. The origin is an implementation detail and appears nowhere in the
// UI except the new-tab link, which has to be a real URL.
// ?cat=sports lands the player on the SPORTS pill with the channel list
// already expanded, rather than on ALL with the list collapsed.
const PLAYER_URL = 'https://prism-tv.pages.dev/?cat=sports'

// Opens EXPANDED on the homepage. The iframe still mounts only while open,
// so collapsing it continues to unmount the player and stop audio.
export default function PrismPlayerPanel({ defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen)
  const [loaded, setLoaded] = useState(false)
  const frameRef = useRef(null)

  const toggle = useCallback(() => {
    setOpen((v) => {
      if (v) setLoaded(false)   // unmounting the frame stops playback
      return !v
    })
  }, [])

  return (
    <section className="ppp-root" aria-labelledby="ppp-h">
      <button
        className={`ppp-head ${open ? 'is-open' : ''}`}
        onClick={toggle}
        aria-expanded={open}
        aria-controls="ppp-body"
      >
        {/* The alpha IS the logo — same glyph and treatment as the homepage
            wordmark (HomePage.jsx:64, .hp-alpha) and the chip loader. */}
        <span id="ppp-h" className="ppp-title">
          <span className="ppp-alpha" aria-hidden="true">&#x03B1;</span>
          <span className="ppp-word">bet</span>
          <span className="ppp-tv">TV</span>
        </span>
        <span className="ppp-sub">40,766 live channels</span>
        <ChevronDown size={16} className="ppp-chev" aria-hidden="true" />
      </button>

      {open && (
        <div className="ppp-body" id="ppp-body">
          {!loaded && (
            <div className="ppp-loading" role="status" aria-live="polite">
              <AbetChipLoader message="Loading ABET TV…" size={68} />
            </div>
          )}
          <iframe
            ref={frameRef}
            className="ppp-frame"
            src={PLAYER_URL}
            title="ABET TV player"
            onLoad={() => setLoaded(true)}
            /* allow list is deliberately narrow: what a video player needs and
               nothing more. No same-origin escape, no top-level navigation. */
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            referrerPolicy="no-referrer"
            loading="eager"
          />
          <div className="ppp-foot">
            <a href={PLAYER_URL} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={12} /> Open ABET TV in a new tab
            </a>
          </div>
        </div>
      )}
    </section>
  )
}
